import FilePicker from '@/components/atoms/FilePicker';
import iconsPng from '@/assets/img/png/icons';
import { CONTENT_THUMBNAIL_MIN_HEIGHT, CONTENT_THUMBNAIL_MIN_WIDTH } from '@/constants/contentLimits';
import { TContentDomain, TContentType } from '@/types/Content.types';
import { Box, Flex, HStack, Image, Text, VStack } from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';
import ContentStatusBadge, { TContentStatusBadgeValue } from './ContentStatusBadge';
import ContentTypeTag from './ContentTypeTag';
import DomainIconTiles from './DomainIconTiles';

type Props = {
  type: TContentType;
  title: string;
  description?: string;
  authorLabel: string;
  domains?: TContentDomain[];
  existingUrl?: string | null;
  file: File | null;
  onFileChange: (file: File | null) => void;
  isPublic: boolean;
  // Read-only mode (e.g. the preview step): no click-to-upload, no remove, no controls.
  readOnly?: boolean;
  // When set, a status chip is overlaid at the bottom of the card (the content list).
  status?: TContentStatusBadgeValue | null;
};

// Thumbnail card mirroring the creator-flow design: a gradient placeholder with the
// title + author overlay until an image is chosen, then the image with the same
// overlay. Clicking the card opens the file picker (unless readOnly).
const ThumbnailPreview = ({
  type,
  title,
  description,
  authorLabel,
  domains = [],
  existingUrl,
  file,
  onFileChange,
  isPublic,
  readOnly = false,
  status,
}: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);

  // Derive a displayable URL straight from the picked file, so the card renders the
  // image even without the FilePicker preview (e.g. after navigating back, or on the
  // read-only preview step). Created in an effect (not useMemo) so the URL is recreated
  // on every mount — StrictMode's mount→unmount→remount would otherwise revoke it.
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!file) {
      setFileUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setFileUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const image = preview || fileUrl || existingUrl || undefined;
  const hasImage = Boolean(image);

  const openPicker = () => inputRef.current?.click();

  const removeImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    onFileChange(null);
    setPreview(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <VStack align="stretch" gap="8px" width="400px" cursor={'pointer'}>
      <Box
        position="relative"
        height="400px"
        borderRadius="14px"
        overflow="hidden"
        cursor={readOnly ? 'default' : 'pointer'}
        onClick={readOnly ? undefined : openPicker}
        backgroundImage={
          hasImage ? undefined : 'linear-gradient(180deg, #4E1AEF 0%, #835FF4 20%, #CABAFA 40%, #FFFFFF 60%)'
        }
        backgroundSize="cover"
        backgroundPosition="center"
      >
        {/* Gradient cards: on hover (dashboard group) the purple gradient floods the
            whole card. A separate full-card layer fades in since CSS can't transition
            background-image. No-op when there's an image or outside a [role=group]. */}
        {!hasImage && (
          <Box
            position="absolute"
            inset={0}
            backgroundImage="linear-gradient(180deg, #4E1AEF 0%, #835FF4 40%, #A68CF7 70%, #CABAFA 100%)"
            opacity={0}
            transition="opacity 0.45s ease"
            _groupHover={{ opacity: 1 }}
          />
        )}

        {/* Real <img> layer — renders both data URLs (create preview) and remote URLs.
            Gentle zoom on hover, but only when rendered inside a [role=group] ancestor
            (the dashboard cards) — no-op in the setup/preview wizard. */}
        {hasImage && (
          <Image
            src={image}
            alt={title}
            loading="lazy"
            decoding="async"
            position="absolute"
            inset={0}
            width="100%"
            height="100%"
            objectFit="cover"
            transition="transform 0.45s ease"
            _groupHover={{ transform: 'scale(1.06)' }}
          />
        )}

        {/* Readability scrim over images — semi-transparent so the image stays visible. */}
        {hasImage && (
          <Box
            position="absolute"
            inset={0}
            backgroundImage="linear-gradient(180deg, #4E1AEF 0%, rgba(78, 26, 239, 0) 100%)"
          />
        )}

        {/* All overlay content lives in one padded container — adjust spacing here. */}
        <Flex position="absolute" inset={0} direction="column" padding="24px" gap="0px">
          {/* Top: type tag + domain badges, then title + author */}
          <VStack align="flex-start" gap={'8px'} flex={1} minWidth={0}>
            <HStack justifyContent={'space-between'} width={'full'}>
              <ContentTypeTag type={type} />
              {domains.length > 0 && (
                <Box flexShrink={0}>
                  <DomainIconTiles domains={domains} />
                </Box>
              )}
            </HStack>

            <Text fontSize="22px" fontWeight={600} color="white" width="100%" fontFamily={'Lora'} fontStyle={'italic'}>
              {title || 'Title will go here'}
            </Text>
            <Text fontSize="14px" fontWeight={700} color="whiteAlpha.800">
              {authorLabel}
            </Text>

            {/* Description — full width, below the title/author */}
            {description && (
              <Text fontSize="14px" color="white" fontWeight={600} noOfLines={5}>
                {description}
              </Text>
            )}
          </VStack>

          {/* Upload affordance (editable + no image) fills the remaining space below */}
          {!hasImage && !readOnly && (
            <VStack flex={1} justify="end" gap="0px" marginBottom={'40px'}>
              <Image
                src={iconsPng.uploadThumbnail}
                alt="Upload thumbnail"
                width="80px"
                height="80px"
                objectFit="contain"
              />
              <Text fontSize="12px" fontWeight={900} color="text.mediumGray" textTransform="uppercase">
                Upload thumbnail
              </Text>
              <Text fontSize="11px" color="text.mediumGray">
                PNG, JPG, HEIC · Max 5MB
              </Text>
            </VStack>
          )}
        </Flex>

        {/* Status chip overlaid at the bottom-left of the card (content list). */}
        {status && (
          <Box position="absolute" bottom="24px" right="24px">
            <ContentStatusBadge status={status} />
          </Box>
        )}
      </Box>

      {/* Controls below the card — only in editable mode */}
      {!readOnly && (
        <VStack justify="space-between" align="center" gap="8px">
          <Text fontSize="12px" color="text.mediumGray">
            {isPublic
              ? 'Note: This content will be visible to all organizations.'
              : 'Note: This content will be visible to your organization only.'}
          </Text>
          {hasImage && (
            <Text
              fontSize="12px"
              fontWeight={700}
              color="extra.red"
              cursor="pointer"
              flexShrink={0}
              whiteSpace="nowrap"
              _hover={{ textDecoration: 'underline' }}
              onClick={removeImage}
            >
              Remove thumbnail
            </Text>
          )}
        </VStack>
      )}

      {!readOnly && (
        <FilePicker
          inputRef={inputRef}
          selectedFile={file}
          setSelectedFile={onFileChange}
          setPreview={setPreview}
          accept="image/*"
          fileMinWidthPx={CONTENT_THUMBNAIL_MIN_WIDTH}
          fileMinHeightPx={CONTENT_THUMBNAIL_MIN_HEIGHT}
          fileSizeLimitInMBs={5}
        />
      )}
    </VStack>
  );
};

export default ThumbnailPreview;
